'use client'

import { useConvertNumber, useTranslate } from '@repo/shared/hooks'
import { FormSchema, useAppForm } from '@repo/ui/hooks'

import { useDepositOnlinePaymentSchema } from '@repo/shared/schemas'
import { useGetBanks, UserUserBankAccount } from '@repo/services'
import { convertToCardNumber } from '@repo/tools'
import {
  Card,
  Col,
  Alert,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Row,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from '@repo/ui/components'
import { bankDetailsList } from '@repo/ui/components/bank-card-field/constants'
import { useIsDesktop } from '@repo/ui/hooks'
import { DepositAccountToAccountCTA } from './deposit-account-to-account.cta'
import { useEffect, useMemo } from 'react'
import { DepositOnlinePaymentAddCard } from '../online-payment/deposit-online-payment.add-card'
import { AccountToAccountOutputItem } from './account-to-account.output-item'

type Card = {
  enName: string | undefined
  card: string | undefined
  sheba: string | undefined
  default: boolean
  id: number
}

export const DepositAccountToAccountContainer = () => {
  const { t } = useTranslate()
  const { convertNumberToLetter } = useConvertNumber()
  const { data, isLoading } = useGetBanks({})

  const cards: Card[] = useMemo(
    () =>
      (data?.data?.bankAccounts || [])
        .filter((bank) => bank.type === 'card')
        .map((bank: UserUserBankAccount) => ({
          id: bank.id ?? 0,
          enName: bank.details?.bankName ?? 'Unknown',
          card:
            bank.type === 'card'
              ? convertToCardNumber(Number(bank.data))
              : undefined,
          sheba: bank.details?.iban,
          default: bank.default ?? false,
        })),
    [data?.data?.bankAccounts],
  )
  const defaultCard: Card | undefined = useMemo(
    () => cards.find((c) => c.default),
    [cards],
  )

  const { schema } = useDepositOnlinePaymentSchema()
  const { form } = useAppForm({
    formSchema: schema,
    defaultValues: {
      card: defaultCard ? (defaultCard.sheba ?? '') : undefined,
      amount: undefined,
    },
  })
  useEffect(() => {
    form.setValue('card', defaultCard ? (defaultCard.sheba ?? '') : undefined)
  }, [defaultCard, form])

  const onSubmit = (data: FormSchema<typeof schema>) => {}

  const isDesktop = useIsDesktop()

  const getBankIcon = (enName: string) => {
    const logo = bankDetailsList.find(
      (bank) =>
        bank.enNames.some(
          (name) => name.toLowerCase() === enName.toLowerCase(),
        ) || bank.name.toLowerCase() === enName.toLowerCase(),
    )
    return logo?.icon ?? <Icon name='CardLine' size='sm' />
  }

  const outputItems = [
    {
      title: t('depositAccountToAccountPage_account_number_destination'),
      description: t('depositAccountToAccountPage_allowed_account_hint'),
      value: '18005150596801',
      isCopyable: true,
    },
  ]

  return (
    <Card className='max-w-container-md mx-auto p-xl' withoutBorder>
      {isDesktop && <DepositAccountToAccountCTA origin='container' />}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id='deposit-with-number-form'
        >
          <Col className='gap-3xl'>
            <Alert
              color='alert'
              size='md'
              icon={<Icon name='AlertTriangleLine' size='sm' />}
              caption={t('depositAccountToAccountPage_hint')}
            />
            <FormField
              control={form.control}
              name='card'
              extra={<DepositOnlinePaymentAddCard />}
              render={({ field }) => (
                <Select {...field} onValueChange={field.onChange}>
                  <FormItemWrapper
                    title={t('depositAccountToAccountPage_select_sheba')}
                    caption={t('depositAccountToAccountPage_account_number', {
                      accountNumber: '18005150596801',
                    })}
                    inputSize='md'
                  >
                    <SelectTrigger inputSize='md' className='w-full'>
                      <SelectValue
                        placeholder={t(
                          'depositWithNumberPage_select_card_placeholder',
                        )}
                      />
                    </SelectTrigger>
                  </FormItemWrapper>
                  <SelectContent>
                    {cards.map((c, idx) => (
                      <SelectItem key={idx} value={c.sheba || String(idx)}>
                        <Row className='gap-xs items-center'>
                          {c.enName && getBankIcon(c.enName)}
                          <Typography size='md'>{c.sheba}</Typography>
                        </Row>
                      </SelectItem>
                    ))}
                    {cards.length === 0 && !isLoading && (
                      <Col className='min-h-20 text-center m-auto gap-md'>
                        <Typography
                          size='md'
                          className='m-auto'
                          color='text-neutral-surface-80'
                        >
                          {t('depositOnlinePaymentPage_select_card_empty')}
                        </Typography>
                      </Col>
                    )}
                  </SelectContent>
                </Select>
              )}
            />

            {outputItems.map((item) => (
              <AccountToAccountOutputItem key={item.title} {...item} />
            ))}

            <Alert
              color='base'
              size='md'
              title={t('depositWithNumberPage_estimated_fee', {
                fee: '0.01',
                amount: '100000000',
              })}
              hideIcon
              caption={t(
                'depositWithNumberPage_estimated_fee_hint_account_to_account',
                {
                  fee: convertNumberToLetter((0.01 / 100) * 100000000),
                  amount: convertNumberToLetter(100000000),
                },
              )}
            />
          </Col>
        </form>
      </Form>
    </Card>
  )
}
